import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';

const C = { teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981', error:'#EF4444', border:'#E5E7EB', bg:'#F0F7FA', grey:'#6B7280', dark:'#1F2937', warning:'#F59E0B' };
const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const HOURS = [8,9,10,11,12,13,14,15,16,17,18,19,20];

const LESSON_TYPES = [
  {
    val: 'human_tutor',
    label: '👤 1-on-1 Human Tutor',
    desc: 'Private session, 1-on-1 with your tutor',
    badge: 'Private',
  },
  {
    val: 'hybrid',
    label: '🤖👤 Hybrid',
    desc: 'AI-assisted prep + tutor review session',
    badge: 'Hybrid',
  },
];

function getWeekDates(offsetWeeks = 0) {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1) + offsetWeeks * 7;
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(diff + i);
    return d;
  });
}
function fmt(date) { return date.toISOString().split('T')[0]; }
function fmtDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
}

// ── Step indicator ────────────────────────────────────────────────────────────
function Steps({ current }) {
  const steps = ['Choose Tutor', 'Pick Slot', 'Pay & Confirm'];
  return (
    <div style={{ display:'flex', alignItems:'center', gap:0, marginBottom:28 }}>
      {steps.map((s, i) => (
        <React.Fragment key={s}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div style={{
              width:28, height:28, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:13, fontWeight:700,
              background: i < current ? C.success : i === current ? C.teal : '#E5E7EB',
              color: i <= current ? '#fff' : C.grey,
            }}>{i < current ? '✓' : i + 1}</div>
            <span style={{ fontSize:13, fontWeight: i === current ? 700 : 400, color: i === current ? C.dark : C.grey }}>{s}</span>
          </div>
          {i < steps.length - 1 && <div style={{ flex:1, height:2, background: i < current ? C.success : '#E5E7EB', margin:'0 10px' }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function BookingPage() {
  const navigate = useNavigate();
  // Free assessment / mock-test candidate accounts can't book paid sessions.
  useEffect(() => {
    if (getUser()?.is_candidate) navigate('/my-assessment', { replace: true });
  }, [navigate]);
  const [step, setStep]               = useState(0); // 0=choose tutor, 1=pick slot, 2=pay
  const [tutors, setTutors]           = useState([]);
  const [selectedTutor, setTutor]     = useState(null);
  const [lessonType, setLessonType]   = useState('human_tutor');
  const [pricing, setPricing]         = useState(null);  // { price, currency, duration_minutes }
  const [availability, setAvail]      = useState({});
  const [weekOffset, setWeekOffset]   = useState(0);
  const [selectedSlot, setSlot]       = useState(null);  // { date, hour }
  const [notes, setNotes]             = useState('');
  const [bankDetails, setBankDetails] = useState(null);
  const [proofFile, setProofFile]     = useState(null);
  const [uploading, setUploading]     = useState(false);
  const [requestId, setRequestId]     = useState(null);
  const [done, setDone]               = useState(false);
  const [myRequests, setMyRequests]   = useState([]);
  const [tab, setTab]                 = useState('book');
  const [loading, setLoading]         = useState(false);
  const [resubmitId, setResubmitId]   = useState(null);   // request id currently being resubmitted
  const [resubmitFile, setResubmitFile] = useState(null);
  const [resubmitting, setResubmitting] = useState(false);
  const [promoCode, setPromoCode]     = useState('');
  const [promoResult, setPromoResult] = useState(null);  // null | { valid, discount_amt, final_price, promo_name, ... }
  const [promoLoading, setPromoLoading] = useState(false);

  const weekDates = getWeekDates(weekOffset);

  // Load tutors
  useEffect(() => {
    api.get('/api/student/tutors').then(r => setTutors(r.data.tutors || [])).catch(() => {});
    api.get('/api/student/payment-info').then(r => setBankDetails(r.data?.bank_details)).catch(() => {});
  }, []);

  // Load my requests
  // Load my requests + auto-refresh every 30s while on my-bookings tab
  useEffect(() => {
    if (tab !== 'my-bookings') return;
    const fetchRequests = () =>
      api.get('/api/student/extra-bookings').then(r => setMyRequests(r.data.requests || [])).catch(() => {});
    fetchRequests();
    const poll = setInterval(fetchRequests, 30000);
    return () => clearInterval(poll);
  }, [tab]);

  // Load pricing when lesson type changes
  useEffect(() => {
    api.get(`/api/student/extra-bookings/price?lesson_type=${lessonType}`)
      .then(r => setPricing(r.data)).catch(() => {});
  }, [lessonType]);

  // Load availability when tutor selected
  useEffect(() => {
    if (!selectedTutor) return;
    const start = fmt(weekDates[0]);
    const end   = fmt(weekDates[6]);
    api.get(`/api/student/tutors/${selectedTutor.id}/availability?start=${start}&end=${end}`)
      .then(r => setAvail(r.data.availability || {})).catch(() => setAvail({}));
  }, [selectedTutor, weekOffset]);

  // ── Promo code validation ────────────────────────────────────────────────
  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await api.get('/api/student/extra-bookings/validate-promo', {
        params: { code: promoCode.trim().toUpperCase(), lesson_type: lessonType }
      });
      setPromoResult(res.data);
    } catch (err) {
      setPromoResult({ valid: false, error: 'Could not validate code.' });
    }
    setPromoLoading(false);
  };

  const effectivePrice = promoResult?.valid ? promoResult.final_price : pricing?.price;

  // ── Step 2: Create the request (without proof) ──────────────────────────
  const handleCreateRequest = async () => {
    if (!selectedSlot || !selectedTutor) return;
    setLoading(true);
    try {
      const res = await api.post('/api/student/extra-bookings', {
        tutor_id:     selectedTutor.id,
        booking_date: selectedSlot.date,
        time_slot:    selectedSlot.hour,
        lesson_type:  lessonType,
        notes:        notes,
        promo_code:   promoResult?.valid ? promoCode.trim().toUpperCase() : '',
      });
      setRequestId(res.data.request.id);
      setStep(2);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create request.');
    }
    setLoading(false);
  };

  // ── Step 3: Upload proof ─────────────────────────────────────────────────
  const handleUploadProof = async () => {
    if (!proofFile || !requestId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', proofFile);
      await api.post(`/api/student/extra-bookings/${requestId}/upload-proof`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDone(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Upload failed. Please try again.');
    }
    setUploading(false);
  };

  // ── Cancel a pending/submitted request ──────────────────────────────────
  const handleCancelRequest = async (reqId) => {
    if (!window.confirm('Cancel this booking request? This cannot be undone.')) return;
    try {
      await api.post(`/api/student/extra-bookings/${reqId}/cancel`);
      const res = await api.get('/api/student/extra-bookings');
      setMyRequests(res.data.requests || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not cancel. Please contact your school.');
    }
  };

  // ── Resubmit proof for a rejected request ───────────────────────────────
  const handleResubmit = async (reqId) => {
    if (!resubmitFile) return;
    setResubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', resubmitFile);
      await api.post(`/api/student/extra-bookings/${reqId}/upload-proof`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResubmitId(null);
      setResubmitFile(null);
      // Refresh list
      const res = await api.get('/api/student/extra-bookings');
      setMyRequests(res.data.requests || []);
    } catch (err) {
      alert(err.response?.data?.error || 'Resubmit failed. Please try again.');
    }
    setResubmitting(false);
  };

  const ltInfo = LESSON_TYPES.find(l => l.val === lessonType);

  // ── Done screen ──────────────────────────────────────────────────────────
  if (done) {
    return (
      <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <motion.div initial={{ scale:0.85, opacity:0 }} animate={{ scale:1, opacity:1 }}
          style={{ background:'#fff', borderRadius:20, padding:48, textAlign:'center', maxWidth:500, boxShadow:'0 4px 24px rgba(0,0,0,0.1)' }}>
          <div style={{ fontSize:64, marginBottom:12 }}>⏳</div>
          <h2 style={{ margin:'0 0 8px', fontSize:22, fontWeight:800, color:C.dark }}>Payment Submitted!</h2>
          <p style={{ color:C.grey, marginBottom:20, lineHeight:1.6 }}>
            Your payment proof has been received. Once admin confirms your payment,
            the session will be booked and your tutor will be notified.
          </p>
          <div style={{ background:C.bg, borderRadius:12, padding:20, marginBottom:24, textAlign:'left' }}>
            {[
              { label:'Tutor',    val: selectedTutor?.full_name },
              { label:'Date',     val: fmtDate(selectedSlot?.date) },
              { label:'Time',     val: `${selectedSlot?.hour}:00` },
              { label:'Type',     val: ltInfo?.label },
              { label:'Amount',   val: pricing ? `${pricing.currency} ${pricing.price?.toFixed(2)}` : '—' },
            ].map(({ label, val }) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0', borderBottom:'1px solid #F3F4F6' }}>
                <span style={{ fontSize:13, color:C.grey }}>{label}</span>
                <span style={{ fontSize:13, fontWeight:600 }}>{val}</span>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:12 }}>
            <button onClick={() => { setDone(false); setStep(0); setSlot(null); setProofFile(null); setRequestId(null); setTab('my-bookings'); }}
              style={{ flex:1, padding:12, background:'#F3F4F6', border:`1px solid ${C.border}`, borderRadius:10, fontWeight:600, cursor:'pointer' }}>
              My Bookings
            </button>
            <button onClick={() => navigate('/dashboard')}
              style={{ flex:1, padding:12, background:C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:600, cursor:'pointer' }}>
              Dashboard
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const statusColor = (s) => ({ confirmed:'#D1FAE5', submitted:'#FEF9C3', pending:'#EFF6FF', rejected:'#FEE2E2', cancelled:'#F3F4F6' }[s] || '#F3F4F6');
  const statusText  = (s) => ({ confirmed:'#065F46', submitted:'#92400E', pending:'#1D4ED8', rejected:'#991B1B', cancelled:'#6B7280' }[s] || C.grey);

  return (
    <div style={{ minHeight:'100vh', background:C.bg }}>
      {/* Header */}
      <div style={{ background:C.navy, padding:'14px 24px', display:'flex', alignItems:'center', gap:16 }}>
        <button onClick={() => navigate('/dashboard')}
          style={{ background:'rgba(255,255,255,0.1)', border:'none', color:'#fff', padding:'7px 14px', borderRadius:8, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
          <ArrowLeftIcon style={{ width:14, height:14 }} /> Dashboard
        </button>
        <div style={{ color:'#fff', fontWeight:700, fontSize:17 }}>📅 Book an Extra Session</div>
        <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
          {['book','my-bookings'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding:'7px 16px', borderRadius:8, border:'none', cursor:'pointer', fontWeight:600, fontSize:13,
                background: tab===t ? C.teal : 'rgba(255,255,255,0.1)', color: tab===t ? '#fff' : 'rgba(255,255,255,0.75)' }}>
              {t === 'book' ? '+ New Booking' : '📋 My Bookings'}
            </button>
          ))}
        </div>
      </div>

      {tab === 'my-bookings' ? (
        /* ── My Bookings tab ──────────────────────────────────────────────── */
        <div style={{ maxWidth:800, margin:'32px auto', padding:'0 20px' }}>
          <h3 style={{ fontWeight:700, marginBottom:16, color:C.dark }}>My Extra Session Requests</h3>
          {myRequests.length === 0 ? (
            <div style={{ background:'#fff', borderRadius:12, padding:40, textAlign:'center', color:C.grey }}>
              No extra sessions booked yet.{' '}
              <button onClick={() => setTab('book')} style={{ color:C.teal, background:'none', border:'none', cursor:'pointer', fontWeight:600 }}>Book your first →</button>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {myRequests.map(r => (
                <div key={r.id} style={{ background:'#fff', borderRadius:12, padding:20, display:'flex', alignItems:'center', gap:16, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ width:44, height:44, borderRadius:'50%', background:C.teal+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>
                    {r.lesson_type === 'hybrid' ? '🤖' : '👤'}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14 }}>{r.tutor_name}</div>
                    <div style={{ color:C.grey, fontSize:13 }}>{fmtDate(r.booking_date)} · {r.time_slot}:00 · {r.duration_minutes} min</div>
                    <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>
                      {r.lesson_type === 'hybrid' ? '🤖👤 Hybrid' : '👤 1-on-1 Tutor'} · {r.currency} {r.price?.toFixed(2)}
                    </div>
                    {r.rejection_reason && (
                      <div style={{ fontSize:12, color:'#991B1B', marginTop:4 }}>Reason: {r.rejection_reason}</div>
                    )}
                    {['pending','submitted'].includes(r.payment_status) && (
                      <button
                        onClick={() => handleCancelRequest(r.id)}
                        style={{ marginTop:8, padding:'6px 14px', background:'#FEE2E2', border:'1px solid #FECACA',
                          borderRadius:8, fontSize:12, fontWeight:600, color:'#991B1B', cursor:'pointer' }}>
                        ✕ Cancel Request
                      </button>
                    )}
                    {r.payment_status === 'confirmed' && (
                      <div style={{ fontSize:12, color:'#6B7280', marginTop:6, fontStyle:'italic' }}>
                        To cancel this confirmed session, please contact your school.
                      </div>
                    )}
                    {r.payment_status === 'cancelled' && (
                      <div style={{ fontSize:12, color:'#6B7280', marginTop:4, fontStyle:'italic' }}>
                        This request was cancelled.
                      </div>
                    )}
                    {r.payment_status === 'rejected' && resubmitId !== r.id && (
                      <button
                        onClick={() => { setResubmitId(r.id); setResubmitFile(null); }}
                        style={{ marginTop:8, padding:'6px 14px', background:'#FEF3C7', border:'1px solid #F59E0B',
                          borderRadius:8, fontSize:12, fontWeight:600, color:'#92400E', cursor:'pointer' }}>
                        📎 Resubmit Payment Proof
                      </button>
                    )}
                    {resubmitId === r.id && (
                      <div style={{ marginTop:10, padding:14, background:'#F8FAFC', borderRadius:10, border:'1px solid #E5E7EB' }}>
                        <div style={{ fontSize:12, color:C.grey, marginBottom:8 }}>Upload new payment proof:</div>
                        <input type="file" accept="image/*,application/pdf"
                          onChange={e => setResubmitFile(e.target.files?.[0] || null)}
                          style={{ display:'block', marginBottom:10, fontSize:12 }} />
                        <div style={{ display:'flex', gap:8 }}>
                          <button onClick={() => handleResubmit(r.id)} disabled={!resubmitFile || resubmitting}
                            style={{ padding:'7px 16px', background: resubmitFile ? C.teal : '#E5E7EB',
                              color: resubmitFile ? '#fff' : C.grey, border:'none', borderRadius:8,
                              fontSize:12, fontWeight:700, cursor: resubmitFile ? 'pointer' : 'not-allowed',
                              opacity: resubmitting ? 0.7 : 1 }}>
                            {resubmitting ? 'Uploading...' : '✅ Submit'}
                          </button>
                          <button onClick={() => { setResubmitId(null); setResubmitFile(null); }}
                            style={{ padding:'7px 14px', background:'#F3F4F6', border:'none', borderRadius:8,
                              fontSize:12, color:C.grey, cursor:'pointer' }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <span style={{
                    padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600,
                    background: statusColor(r.payment_status),
                    color:       statusText(r.payment_status),
                  }}>{r.payment_status.charAt(0).toUpperCase() + r.payment_status.slice(1)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* ── Book tab ─────────────────────────────────────────────────────── */
        <div style={{ maxWidth:900, margin:'24px auto', padding:'0 20px' }}>
          <Steps current={step} />

          {/* ── STEP 0: Choose Tutor + Lesson Type ──────────────────────── */}
          {step === 0 && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
              {/* Tutors */}
              <div>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:12, color:C.dark }}>Choose a Tutor</div>
                {tutors.length === 0 ? (
                  <div style={{ background:'#fff', borderRadius:12, padding:24, textAlign:'center', color:C.grey, fontSize:14 }}>No tutors available</div>
                ) : (
                  tutors.map(t => (
                    <motion.div key={t.id} whileHover={{ scale:1.01 }}
                      onClick={() => setTutor(t)}
                      style={{ background: selectedTutor?.id===t.id ? C.navy : '#fff', borderRadius:12, padding:16, cursor:'pointer', marginBottom:10,
                        border:`2px solid ${selectedTutor?.id===t.id ? C.teal : C.border}`, transition:'all 0.15s' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:40, height:40, borderRadius:'50%', background:C.teal+'22', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0, overflow:'hidden' }}>
                          {t.photo_url ? <img src={t.photo_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : t.full_name?.charAt(0)}
                        </div>
                        <div>
                          <div style={{ fontWeight:700, color: selectedTutor?.id===t.id ? '#fff' : C.dark, fontSize:14 }}>{t.full_name}</div>
                          <div style={{ fontSize:12, color: selectedTutor?.id===t.id ? 'rgba(255,255,255,0.6)' : C.grey }}>
                            {(t.specializations || []).slice(0,3).join(' · ') || 'General English'}
                          </div>
                        </div>
                        {selectedTutor?.id===t.id && <div style={{ marginLeft:'auto', color:C.teal, fontSize:18 }}>✓</div>}
                      </div>
                    </motion.div>
                  ))
                )}
              </div>

              {/* Lesson type + pricing */}
              <div>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:12, color:C.dark }}>Lesson Type & Pricing</div>
                {LESSON_TYPES.map(opt => (
                  <div key={opt.val} onClick={() => setLessonType(opt.val)}
                    style={{ background: lessonType===opt.val ? '#F0F7FA' : '#fff', border:`2px solid ${lessonType===opt.val ? C.teal : C.border}`,
                      borderRadius:12, padding:16, cursor:'pointer', marginBottom:10, transition:'all 0.15s' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:14, color:C.dark }}>{opt.label}</div>
                        <div style={{ fontSize:12, color:C.grey, marginTop:4 }}>{opt.desc}</div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
                        {pricing && lessonType===opt.val ? (
                          <div style={{ fontWeight:800, fontSize:18, color:C.teal }}>
                            {pricing.currency} {pricing.price?.toFixed(2)}
                          </div>
                        ) : (
                          <div style={{ fontSize:13, color:C.grey }}>per session</div>
                        )}
                        {pricing && lessonType===opt.val && (
                          <div style={{ fontSize:11, color:C.grey }}>{pricing.duration_minutes} min</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <div style={{ background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:10, padding:14, marginTop:8 }}>
                  <div style={{ fontSize:12, color:'#92400E', lineHeight:1.6 }}>
                    💡 <strong>Extra sessions</strong> are charged separately from your course package.
                    Payment proof required — admin will confirm your booking.
                  </div>
                </div>

                <button
                  disabled={!selectedTutor}
                  onClick={() => setStep(1)}
                  style={{ width:'100%', marginTop:20, padding:'13px', background: selectedTutor ? C.teal : '#E5E7EB',
                    color: selectedTutor ? '#fff' : C.grey, border:'none', borderRadius:10, fontWeight:700, fontSize:14,
                    cursor: selectedTutor ? 'pointer' : 'not-allowed', transition:'all 0.2s' }}>
                  {selectedTutor ? `Next: Pick a Time Slot →` : 'Select a tutor to continue'}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 1: Pick Slot ──────────────────────────────────────── */}
          {step === 1 && (
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                <button onClick={() => setStep(0)}
                  style={{ background:'#F3F4F6', border:'none', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontSize:13 }}>
                  ← Back
                </button>
                <div style={{ fontWeight:700, fontSize:15, color:C.dark }}>
                  Pick a slot with {selectedTutor?.full_name}
                </div>
                <div style={{ marginLeft:'auto', background: C.teal+'18', borderRadius:8, padding:'6px 14px', fontSize:13, fontWeight:600, color:C.teal }}>
                  {ltInfo?.label} · {pricing?.currency} {pricing?.price?.toFixed(2)}
                </div>
              </div>

              <div style={{ background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                {/* Week nav */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
                  <button onClick={() => setWeekOffset(w => Math.max(0, w-1))} disabled={weekOffset===0}
                    style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'6px 14px', cursor: weekOffset===0?'not-allowed':'pointer', opacity: weekOffset===0?0.4:1, fontSize:13 }}>
                    ← Prev
                  </button>
                  <div style={{ fontWeight:700, fontSize:14 }}>
                    {weekDates[0].toLocaleDateString('en-GB', { day:'numeric', month:'short' })} –{' '}
                    {weekDates[6].toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}
                  </div>
                  <button onClick={() => setWeekOffset(w => w+1)}
                    style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:8, padding:'6px 14px', cursor:'pointer', fontSize:13 }}>
                    Next →
                  </button>
                </div>

                {/* Grid */}
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ width:48, textAlign:'left', fontSize:11, color:C.grey, paddingBottom:10 }}>Time</th>
                        {weekDates.map((d, i) => (
                          <th key={i} style={{ textAlign:'center', fontSize:11, color:C.grey, paddingBottom:10, minWidth:50 }}>
                            <div>{DAYS[i]}</div>
                            <div style={{ fontWeight:700, color:C.dark, fontSize:14 }}>{d.getDate()}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {HOURS.map(hour => (
                        <tr key={hour}>
                          <td style={{ fontSize:11, color:C.grey, paddingRight:8, verticalAlign:'middle' }}>{hour}:00</td>
                          {weekDates.map((d, di) => {
                            const dateStr = fmt(d);
                            const avail   = (availability[dateStr] || []).includes(hour);
                            const isSel   = selectedSlot?.date===dateStr && selectedSlot?.hour===hour;
                            const isPast  = d < new Date(new Date().setHours(0,0,0,0));
                            return (
                              <td key={di} style={{ padding:'2px', textAlign:'center' }}>
                                {avail && !isPast ? (
                                  <motion.div whileHover={{ scale:1.12 }}
                                    onClick={() => setSlot({ date:dateStr, hour })}
                                    style={{ width:40, height:28, margin:'0 auto', borderRadius:6, cursor:'pointer',
                                      background: isSel ? C.teal : '#E0F7FA',
                                      border:`2px solid ${isSel ? '#0087A8' : '#B2EBF2'}`,
                                      display:'flex', alignItems:'center', justifyContent:'center',
                                      fontSize:11, fontWeight:600, color: isSel ? '#fff' : '#0087A8',
                                      transition:'all 0.12s' }}>
                                    {isSel ? '✓' : 'Free'}
                                  </motion.div>
                                ) : (
                                  <div style={{ width:40, height:28, margin:'0 auto', borderRadius:6, background:'#F9FAFB', border:`1px solid ${C.border}` }} />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {Object.keys(availability).length === 0 && (
                  <div style={{ textAlign:'center', color:C.grey, paddingTop:20, fontSize:13 }}>
                    No availability set for this week. Try another week.
                  </div>
                )}

                <AnimatePresence>
                  {selectedSlot && (
                    <motion.div initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }}
                      style={{ marginTop:20, background:'#F0FDF4', borderRadius:12, padding:18, border:'1px solid #86EFAC' }}>
                      <div style={{ fontWeight:700, marginBottom:10, color:C.dark }}>
                        ✅ Selected: {fmtDate(selectedSlot.date)} at {selectedSlot.hour}:00
                      </div>
                      <textarea value={notes} onChange={e => setNotes(e.target.value)}
                        placeholder="Notes for tutor (e.g. 'I want to practise IELTS writing task 2')"
                        rows={2} style={{ width:'100%', padding:'10px 14px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, resize:'none', fontFamily:'inherit', boxSizing:'border-box', marginBottom:12 }} />
                      <button onClick={handleCreateRequest} disabled={loading}
                        style={{ width:'100%', padding:'13px', background:C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer', opacity: loading ? 0.7 : 1 }}>
                        {loading ? 'Processing...' : `Continue to Payment →`}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}

          {/* ── STEP 2: Pay ────────────────────────────────────────────── */}
          {step === 2 && (
            <div style={{ maxWidth:560, margin:'0 auto' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
                <button onClick={() => setStep(1)}
                  style={{ background:'#F3F4F6', border:'none', borderRadius:8, padding:'8px 14px', cursor:'pointer', fontSize:13 }}>
                  ← Back
                </button>
                <div style={{ fontWeight:700, fontSize:15, color:C.dark }}>Payment</div>
              </div>

              {/* Order summary */}
              <div style={{ background:'#fff', borderRadius:14, padding:24, marginBottom:16, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:16, color:C.dark }}>Order Summary</div>
                {[
                  { label:'Tutor',    val: selectedTutor?.full_name },
                  { label:'Date',     val: selectedSlot ? fmtDate(selectedSlot.date) : '' },
                  { label:'Time',     val: selectedSlot ? `${selectedSlot.hour}:00` : '' },
                  { label:'Duration', val: pricing ? `${pricing.duration_minutes} min` : '' },
                  { label:'Type',     val: ltInfo?.label },
                ].map(({ label, val }) => (
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:`1px solid ${C.border}` }}>
                    <span style={{ fontSize:13, color:C.grey }}>{label}</span>
                    <span style={{ fontSize:13, fontWeight:600, color:C.dark }}>{val}</span>
                  </div>
                ))}
                {promoResult?.valid && (
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:`1px solid ${C.border}` }}>
                    <span style={{ fontSize:13, color:'#15803D' }}>🏷 {promoResult.promo_name} ({promoResult.discount_pct}% off)</span>
                    <span style={{ fontSize:13, fontWeight:600, color:'#15803D' }}>-{pricing?.currency} {promoResult.discount_amt?.toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display:'flex', justifyContent:'space-between', padding:'12px 0', borderTop:`2px solid ${C.border}`, marginTop:4 }}>
                  <span style={{ fontWeight:700, fontSize:15 }}>Total</span>
                  <div style={{ textAlign:'right' }}>
                    {promoResult?.valid && (
                      <div style={{ fontSize:12, color:C.grey, textDecoration:'line-through' }}>
                        {pricing?.currency} {pricing?.price?.toFixed(2)}
                      </div>
                    )}
                    <span style={{ fontWeight:800, fontSize:20, color:C.teal }}>
                      {pricing ? `${pricing.currency} ${(effectivePrice ?? pricing.price)?.toFixed(2)}` : '—'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Promo code */}
              <div style={{ background:'#fff', borderRadius:14, padding:24, marginBottom:16, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:12, color:C.dark }}>🏷 Promo Code</div>
                <div style={{ display:'flex', gap:10 }}>
                  <input
                    value={promoCode}
                    onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoResult(null); }}
                    placeholder="Enter promo code"
                    style={{ flex:1, padding:'10px 14px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, textTransform:'uppercase', letterSpacing:1 }}
                  />
                  <button onClick={validatePromo} disabled={!promoCode.trim() || promoLoading}
                    style={{ padding:'10px 18px', background:C.teal, color:'#fff', border:'none', borderRadius:8,
                      fontWeight:700, fontSize:13, cursor: promoCode.trim() ? 'pointer' : 'not-allowed',
                      opacity: promoLoading ? 0.7 : 1 }}>
                    {promoLoading ? '...' : 'Apply'}
                  </button>
                </div>
                {promoResult && (
                  <div style={{ marginTop:10, fontSize:13, padding:'8px 12px', borderRadius:8,
                    background: promoResult.valid ? '#DCFCE7' : '#FEE2E2',
                    color: promoResult.valid ? '#15803D' : '#B91C1C' }}>
                    {promoResult.valid
                      ? `✓ ${promoResult.promo_name} — ${promoResult.discount_pct}% discount applied!`
                      : promoResult.error || 'Invalid promo code'}
                  </div>
                )}
              </div>

              {/* Bank details */}
              {bankDetails && (bankDetails.bank_name || bankDetails.account_number) ? (
                <div style={{ background:'#fff', borderRadius:14, padding:24, marginBottom:16, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontWeight:700, fontSize:15, marginBottom:14, color:C.dark }}>💳 Bank Transfer Details</div>
                  <div style={{ background:'#F8FAFC', borderRadius:10, padding:16, fontSize:13, lineHeight:2 }}>
                    {bankDetails.bank_name && <div><b>Bank:</b> {bankDetails.bank_name}</div>}
                    {bankDetails.account_holder && <div><b>Account Name:</b> {bankDetails.account_holder}</div>}
                    {bankDetails.account_number && <div><b>Account Number:</b> {bankDetails.account_number}</div>}
                    {bankDetails.sort_code && <div><b>Sort Code:</b> {bankDetails.sort_code}</div>}
                    {bankDetails.swift_bic && <div><b>SWIFT/BIC:</b> {bankDetails.swift_bic}</div>}
                  </div>
                  <div style={{ marginTop:12, fontSize:12, color:C.grey, lineHeight:1.6 }}>
                    Please include your name and the date <strong>{selectedSlot?.date}</strong> as the payment reference.
                  </div>
                </div>
              ) : (
                <div style={{ background:'#FEF9C3', border:'1px solid #FDE68A', borderRadius:12, padding:16, marginBottom:16 }}>
                  <div style={{ fontSize:13, color:'#92400E' }}>⚠️ Bank details not configured. Please contact your school for payment instructions.</div>
                </div>
              )}

              {/* Upload proof */}
              <div style={{ background:'#fff', borderRadius:14, padding:24, boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ fontWeight:700, fontSize:15, marginBottom:14, color:C.dark }}>📎 Upload Payment Proof</div>
                <div style={{ fontSize:13, color:C.grey, marginBottom:16 }}>
                  Upload a screenshot or receipt of your bank transfer to complete the booking request.
                </div>
                <input type="file" accept="image/*,application/pdf"
                  onChange={e => setProofFile(e.target.files?.[0] || null)}
                  style={{ display:'block', marginBottom:16, fontSize:13 }} />
                {proofFile && (
                  <div style={{ fontSize:12, color:C.grey, marginBottom:12 }}>
                    Selected: <strong>{proofFile.name}</strong> ({(proofFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
                <button onClick={handleUploadProof} disabled={!proofFile || uploading}
                  style={{ width:'100%', padding:'13px', background: proofFile ? C.success : '#E5E7EB',
                    color: proofFile ? '#fff' : C.grey, border:'none', borderRadius:10, fontWeight:700, fontSize:14,
                    cursor: proofFile ? 'pointer' : 'not-allowed', opacity: uploading ? 0.7 : 1 }}>
                  {uploading ? 'Uploading...' : proofFile ? '✅ Submit Payment Proof' : 'Choose a file first'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
